import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import useSEO from "../Hooks/UseSeo";
import { Helmet } from "react-helmet";

const DelhiToNainital = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: "Delhi to Nainital Cab @3349 | Taxi Service from Delhi to Nainital - Pick Quick Cabs",
    description:
      "Book reliable and affordable Delhi to Nainital cabs with Pick Quick Cabs. Enjoy a comfortable ride through the scenic hills of Uttarakhand with professional drivers. One-way & round-trip available.",
    url: "https://www.pickquickcabs.com/delhi-to-nainital-cab",
  });

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.pickquickcabs.com/delhi-to-nanital-taxi-service" />
      </Helmet>
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-12 mt-32">
        {/* Hero Section */}
        <section className="relative bg-gray-100 rounded-lg overflow-hidden">
          <img
            src="https://s7ap1.scene7.com/is/image/incredibleindia/naini-lake-nainital-uttarakhand-2-attr-hero?qlt=82&ts=1742153737104"
            alt="Delhi to Nainital Cab Service"
            className="w-full h-80 object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center">
              Delhi to Nainital Cab Service
            </h1>
          </div>
        </section>

        {/* Intro Section */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Delhi to Nainital Taxi Booking</h2>
          <p>
            Planning a getaway from <b>Delhi to Nainital</b>? Book your trip with{" "}
            <b>Pick Quick Cabs</b> and travel to one of Uttarakhand’s most charming hill stations
            in comfort and style. Whether it’s a family vacation, honeymoon, or a short weekend escape,
            our <b>Delhi to Nainital cab service</b> ensures a smooth and safe journey.
          </p>
          <p>
            Experience the scenic beauty of the Kumaon hills as you travel through the plains and
            mountain roads via <b>NH9</b>. Choose from Sedans, SUVs, or Ertiga — all driven by professional
            chauffeurs ensuring a hassle-free ride.
          </p>
        </section>

        {/* Travel Overview */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <img
            src="https://dynamic-media-cdn.tripadvisor.com/media/photo-o/2d/2a/6e/96/caption.jpg?w=900&h=-1&s=1"
            alt="Nainital Lake"
            className="w-full h-72 object-cover rounded-lg shadow"
          />
          <div>
            <h3 className="text-xl font-semibold">About Nainital</h3>
            <p className="mt-2">
              <b>Nainital</b>, known as the <b>‘Lake District of India’</b>, is a beautiful hill station
              nestled around the emerald Naini Lake. Surrounded by lush green hills, it offers a
              tranquil escape with picturesque landscapes and pleasant weather throughout the year.
            </p>
            <ul className="list-disc pl-5 mt-3 space-y-1">
              <li><b>Distance:</b> Approx. 300 km from Delhi</li>
              <li><b>Travel Time:</b> Around 7–8 hours by road</li>
              <li><b>Route:</b> Delhi – Moradabad – Rampur – Haldwani – Nainital</li>
              <li><b>Best Time to Visit:</b> March to June, October to December</li>
            </ul>
          </div>
        </section>

        {/* Nearby Attractions */}
        <section className="space-y-6">
          <h3 className="text-2xl font-semibold text-center">Top Attractions in Nainital</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border rounded-lg overflow-hidden shadow">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/2/28/The_Boat_and_The_Lake.jpg"
                alt="Naini Lake"
                className="w-full h-48 object-cover"
              />
              <div className="p-3">
                <h4 className="font-semibold text-lg">Naini Lake</h4>
                <p className="text-sm mt-1">
                  The heart of Nainital, this serene lake is perfect for boating, photography, and evening strolls.
                </p>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden shadow">
              <img
                src="https://dynamic-media-cdn.tripadvisor.com/media/photo-o/0d/fe/a8/04/view-from-snow-point.jpg?w=1200&h=-1&s=1"
                alt="Snow View Point"
                className="w-full h-48 object-cover"
              />
              <div className="p-3">
                <h4 className="font-semibold text-lg">Snow View Point</h4>
                <p className="text-sm mt-1">
                  Offers panoramic views of the Himalayas and Naini Lake, accessible via ropeway or drive.
                </p>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden shadow">
              <img
                src="https://www.transindiatravels.com/wp-content/uploads/tiffin-top.jpg"
                alt="Tiffin Top"
                className="w-full h-48 object-cover"
              />
              <div className="p-3">
                <h4 className="font-semibold text-lg">Tiffin Top (Dorothy’s Seat)</h4>
                <p className="text-sm mt-1">
                  A famous viewpoint offering breathtaking views of Nainital town and nearby hills.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Travel Info */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-semibold">Distance & Travel Time</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Delhi → Nainital: ~300 km / ~7–8 hrs</li>
              <li>Route: NH9 via Moradabad, Rampur, Haldwani</li>
              <li>Stops: Gajraula, Haldwani, and Kathgodam</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold">Why Choose Us</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Comfortable & well-maintained cars</li>
              <li>Professional and courteous drivers</li>
              <li>Affordable one-way & round-trip packages</li>
              <li>24×7 customer support & online booking</li>
            </ul>
          </div>
        </section>

        {/* Vehicle Options */}
        <section className="space-y-6">
          <h3 className="text-2xl font-semibold">Available Cabs & Fare</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

            <div className="border rounded-lg p-4 flex flex-col items-center space-y-3">
              <img
                src="https://imgd-ct.aeplcdn.com/664x415/n/cw/ec/170299/dzire-2024-right-front-three-quarter.jpeg?isig=0&q=80"
                alt="Sedan"
                className="w-full h-32 object-cover rounded"
              />
              <h4 className="text-lg font-medium">Sedan (Dzire / Similar)</h4>
              <p>Seats: 4+1</p>
              <p>Fare: ₹12.5/km</p>
              <button
                className="mt-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                onClick={() => navigate("/book-a-ride")}
              >
                Book Cab
              </button>
            </div>


            <div className="border rounded-lg p-4 flex flex-col items-center space-y-3">
              <img
                src="https://static3.toyotabharat.com/images/showroom/innova-mmc/silver-color-1600x600.png"
                alt="SUV"
                className="w-full h-32 object-cover rounded"
              />
              <h4 className="text-lg font-medium">SUV (Innova / Similar)</h4>
              <p>Seats: 6+1</p>
              <p>Fare: ₹20/km</p>
              <button
                className="mt-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                onClick={() => navigate("/book-a-ride")}
              >
                Book Cab
              </button>
            </div>


            <div className="border rounded-lg p-4 flex flex-col items-center space-y-3">
              <img
                src="https://htcms-prod-images.s3.ap-south-1.amazonaws.com/htmobile1/marutisuzuki_ertiga/images/colour_marutisuzuki-ertiga_pearl-metallic-arctic-white_600x400.jpg"
                alt="Ertiga"
                className="w-full h-32 object-cover rounded"
              />
              <h4 className="text-lg font-medium">Ertiga</h4>
              <p>Seats: 6+1</p>
              <p>Fare: ₹16/km</p>
              <button
                className="mt-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                onClick={() => navigate("/book-a-ride")}
              >
                Book Cab
              </button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="space-y-4">
          <h3 className="text-2xl font-semibold">Frequently Asked Questions</h3>
          <div className="space-y-3">
            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                What is the fare for a Delhi to Nainital cab?
              </summary>
              <p className="mt-2">
                The fare starts from ₹12.5/km for Sedan and may vary depending on the vehicle type and route.
              </p>
            </details>
            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                Can I book a one-way cab from Delhi to Nainital?
              </summary>
              <p className="mt-2">
                Yes, Pick Quick Cabs provides both one-way and round-trip taxi services from Delhi to Nainital.
              </p>
            </details>
            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                What are some good stopovers on the way to Nainital?
              </summary>
              <p className="mt-2">
                Popular stops include Gajraula for breakfast, Rampur for rest, and Haldwani for sightseeing.
              </p>
            </details>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <p className="text-lg">
            Ready to book your ride? Call us at{" "}
            <a href="tel:+918447433852" className="text-yellow-600 font-semibold">
              +91-8447433852
            </a>
          </p>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default DelhiToNainital;
